import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { approveRequest, listApprovals, rejectRequest } from '@/lib/approvals'
import type { ApprovalStatus } from '@riverpark/types'

export function useApprovals(status?: ApprovalStatus, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['approvals', status ?? 'all'],
    queryFn: () => listApprovals(status),
    enabled: opts?.enabled ?? true,
  })
}

/** For a nav badge — how many pending requests are waiting on this approver right now.
 * Only meaningful for admin/committee; pass `enabled: false` for any other role so the
 * always-mounted Sidebar/BottomNav don't fire this query for roles that can't act on it. */
export function useApprovalsPendingCount(enabled: boolean) {
  const { data } = useApprovals('pending', { enabled })
  return enabled ? (data?.data.length ?? 0) : 0
}

function useInvalidateAfterDecision() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ['approvals'] })
    qc.invalidateQueries({ queryKey: ['invoices'] })
    qc.invalidateQueries({ queryKey: ['plots'] })
    qc.invalidateQueries({ queryKey: ['members'] })
    qc.invalidateQueries({ queryKey: ['notifications'] })
  }
}

export function useApproveRequest() {
  const invalidate = useInvalidateAfterDecision()
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => approveRequest(id, note),
    onSuccess: invalidate,
  })
}

export function useRejectRequest() {
  const invalidate = useInvalidateAfterDecision()
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => rejectRequest(id, note),
    onSuccess: invalidate,
  })
}
